import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ComHubSkeletonLoader from '@company/components/CommunicationHub/ComHubSkeletonLoader';
import ThPagination from '@components/ThPagination/ThPagination';
import FilterBar from '@company/components/CommunicationHub/FilterBar/FilterBar';
import ReviewsAndEndorsementsTable from '@company/components/CommunicationHub/CommunicationHubTables/ReviewsAndEndorsementsTable';
import { getReviewsAndEndorsements } from '@company/services/com-hub/api';
import {
  ComHubQueryParamsEnum,
  ComHubTabs,
  GivenReview,
  WorkerModalParams,
} from '@company/types/comm-hub.type';
import QueryKeys from '@constants/queryKeys';
import { handleComHubSearch, extractComHubUrlParams } from '@utils/ComHubUtils';
import { DEFAULT_ITEMS_PER_PAGE } from '@utils/Pagination';

interface ReviewsAndEndoresementsLeftProps {
  onShowWorkerProfile?: (data: WorkerModalParams) => void;
  onSeeGivenReview: (review: GivenReview) => void;
  onSeeGivenEndorsement: (endorsementCaseId: number) => void;
  onShowAlert?: (success: boolean, message: string) => void;
}

function ReviewsAndEndoresementsLeft({
  onShowWorkerProfile,
  onSeeGivenReview,
  onSeeGivenEndorsement,
  onShowAlert,
}: ReviewsAndEndoresementsLeftProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentOffset, setCurrentOffset] = useState(0);
  const [searchState, setSearchState] = useState<{
    searchText: string;
    dateRange: { startDate: Date | undefined; endDate: Date | undefined };
  }>();
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize state from URL parameters
  useEffect(() => {
    if (isInitialized) return;

    const { searchState: urlSearchState, offset: urlOffset } =
      extractComHubUrlParams(searchParams);

    if (urlSearchState) {
      setSearchState(urlSearchState);
    }

    if (urlOffset !== undefined) {
      setCurrentOffset(urlOffset);
    }

    setIsInitialized(true);
  }, [searchParams, isInitialized]);

  const queryKeyReviewAndEndorsements = [
    QueryKeys.ComHub.GIVEN_REVIEWS,
    currentOffset,
    DEFAULT_ITEMS_PER_PAGE,
    searchState,
  ];

  const {
    data: reviewsLeftResponse,
    isLoading,
    error: reviewsLeftError,
  } = useQuery({
    queryKey: queryKeyReviewAndEndorsements,
    queryFn: () =>
      getReviewsAndEndorsements(
        currentOffset,
        DEFAULT_ITEMS_PER_PAGE,
        searchState?.searchText,
        searchState?.dateRange
      ),
    enabled: isInitialized,
  });

  // Handle query errors
  useEffect(() => {
    if (reviewsLeftError) {
      onShowAlert?.(false, reviewsLeftError.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewsLeftError]);

  const updateSearchParams = useCallback(
    (params: URLSearchParams) => {
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const handleOnSearch = useCallback(
    (search: {
      searchText: string;
      dateRange: { startDate: Date | undefined; endDate: Date | undefined };
    }) => {
      handleComHubSearch(search, setSearchState, updateSearchParams, {
        itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
        setCurrentOffset,
      });
    },
    [updateSearchParams, setCurrentOffset]
  );

  // Update URL when offset changes
  useEffect(() => {
    if (!isInitialized) return;

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(ComHubQueryParamsEnum.OFFSET, currentOffset.toString());
    newSearchParams.set(
      ComHubQueryParamsEnum.MAX,
      DEFAULT_ITEMS_PER_PAGE.toString()
    );
    updateSearchParams(newSearchParams);
  }, [currentOffset, searchParams, updateSearchParams, isInitialized]);

  return (
    <>
      <FilterBar onSearch={handleOnSearch} />
      {isLoading && <ComHubSkeletonLoader />}

      {reviewsLeftResponse?.reviews ? (
        <>
          <ReviewsAndEndorsementsTable
            title={ComHubTabs.REVIEWS_AND_ENDORSEMENTS}
            givenReviews={reviewsLeftResponse.reviews}
            onSeeGivenReview={onSeeGivenReview}
            onSeeGivenEndorsement={onSeeGivenEndorsement}
            onShowWorkerProfile={onShowWorkerProfile}
          />
          <ThPagination
            currentOffset={currentOffset}
            itemsPerPage={DEFAULT_ITEMS_PER_PAGE}
            onPageChange={setCurrentOffset}
            totalItems={reviewsLeftResponse.pagination.count}
          />
        </>
      ) : null}
    </>
  );
}

export default ReviewsAndEndoresementsLeft;
